/**
 * VsCompetitor — generic head-to-head comparison page.
 *
 * Reads competitor data from /app/frontend/src/data/competitors.js keyed by
 * the :slug param. Renders the same hero → TL;DR → feature table → pain
 * bands → FAQ → CTA structure for every competitor so we can add new
 * /vs/{slug} pages with a single data entry (no component duplication).
 *
 * Replaces the legacy VsJobright.js — the /vs/jobright URL still works
 * because 'jobright' is one of the slugs in COMPETITORS.
 *
 * Honesty contract:
 *   Each competitor entry must concede ≥1 row to the competitor. This
 *   page surfaces those concessions in the TL;DR ("Pick {them} if…") and
 *   in the verdict counters — building buyer trust > overselling.
 */
import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, Check, X, Sparkles, Zap, Shield, Award,
  FileText, Target,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { useDocumentMeta } from '../utils/useDocumentMeta';
import { COMPETITORS } from '../data/competitors';

const fadeUp = { initial: { opacity: 0, y: 16 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

const ICONS = { Target, Zap, Shield };

const SITE = 'https://ascendforyou.net';

const buildJsonLd = (slug, data) => ([
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${SITE}/vs/${slug}`,
    name: data.metaTitle,
    description: data.metaDescription,
    url: `${SITE}/vs/${slug}`,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: data.faqs.map(([q, a]) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  },
  {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: data.rows.map((row, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: row.feature,
    })),
  },
]);

const CellIcon = ({ ok }) => ok
  ? <Check size={18} className="text-[#10b981]" aria-label="Yes" />
  : <X size={18} className="text-[#9ca3af]" aria-label="No" />;

const WinnerPill = ({ winner, brand }) => {
  if (winner === 'ascend') return <span className="text-[10px] font-bold uppercase tracking-wider bg-[#002fa7] text-white px-1.5 py-0.5">Ascend</span>;
  if (winner === 'them') return <span className="text-[10px] font-bold uppercase tracking-wider bg-[#0f1115] text-white px-1.5 py-0.5">{brand}</span>;
  return <span className="text-[10px] font-bold uppercase tracking-wider bg-[#e5e7eb] text-[#6b7280] px-1.5 py-0.5">Tie</span>;
};

const VsCompetitor = () => {
  const { slug } = useParams();
  const data = COMPETITORS[slug];

  // Hooks must run unconditionally — keep useDocumentMeta above any
  // early-return guard.
  useDocumentMeta({
    title: data?.metaTitle,
    description: data?.metaDescription,
    path: data ? `/vs/${slug}` : undefined,
    imageAlt: data ? `Honest side-by-side comparison of Ascend and ${data.brand} across 12 features` : undefined,
  });

  if (!data) {
    // Unknown competitor slug → redirect home (no broken page, keeps SEO clean)
    return <Navigate to="/" replace />;
  }

  const ascendWins = data.rows.filter(r => r.winner === 'ascend').length;
  const themWins = data.rows.filter(r => r.winner === 'them').length;
  const ties = data.rows.filter(r => r.winner === 'tie').length;

  return (
    <div className="min-h-screen bg-white" data-testid="vs-competitor-page">
      <SEOHead
        title={data.metaTitle}
        description={data.metaDescription}
        canonical={`${SITE}/vs/${slug}`}
        jsonLd={buildJsonLd(slug, data)}
      />

      {/* Hero */}
      <section className="pt-20 sm:pt-28 pb-12 border-b border-[#e5e7eb]" data-testid="vs-hero">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002fa7] mb-3 block">
            HONEST COMPARISON
          </span>
          <h1 className="font-satoshi text-3xl sm:text-5xl font-black tracking-tighter text-[#0f1115] leading-[1.05]">
            Ascend <span className="text-[#9ca3af] font-normal">vs</span> {data.brand}
          </h1>
          <p className="mt-4 text-base sm:text-lg text-[#4b5563] max-w-2xl mx-auto leading-relaxed">
            {data.hookSentence}
          </p>

          <div className="mt-7 inline-flex items-center gap-2 sm:gap-3 flex-wrap justify-center" data-testid="verdict-counters">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#002fa7] text-white text-xs sm:text-sm font-bold">
              <Award size={14} /> Ascend wins: {ascendWins}
            </span>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#0f1115] text-white text-xs sm:text-sm font-bold">
              {data.brand} wins: {themWins}
            </span>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#e5e7eb] text-[#4b5563] text-xs sm:text-sm font-bold">
              Ties: {ties}
            </span>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to={`/free-ats-check?ref=vs_${slug}&utm_source=vs_${slug}&utm_medium=hero&utm_campaign=comparison`}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#0f1115] text-white text-sm font-bold hover:bg-[#002fa7] transition-colors"
              data-testid="vs-hero-primary-cta"
            >
              <Sparkles size={16} /> Get my free ATS score (no signup)
              <ArrowRight size={16} />
            </Link>
            <Link
              to={`/pricing?ref=vs_${slug}`}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-[#e5e7eb] text-[#0f1115] text-sm font-semibold hover:border-[#002fa7] transition-colors"
              data-testid="vs-hero-secondary-cta"
            >
              See Ascend pricing
            </Link>
          </div>
        </div>
      </section>

      {/* TL;DR */}
      <section className="py-12 bg-[#f9fafb] border-b border-[#e5e7eb]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="font-satoshi text-xs uppercase tracking-[0.2em] font-bold text-[#002fa7] mb-3">TL;DR</h2>
          <div className="grid md:grid-cols-2 gap-4" data-testid="tldr-grid">
            <motion.div {...fadeUp} className="bg-white border border-[#e5e7eb] p-5 border-l-4 border-l-[#002fa7]">
              <div className="flex items-center gap-2 text-[#0f1115] font-bold mb-2">
                <Award size={16} className="text-[#002fa7]" />
                Pick Ascend if…
              </div>
              <ul className="space-y-2 text-sm text-[#4b5563]">
                {data.pickAscendBullets.map((b, i) => (
                  <li key={i} className="flex gap-2">
                    <Check size={14} className="text-[#10b981] mt-0.5 flex-shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div {...fadeUp} className="bg-white border border-[#e5e7eb] p-5 border-l-4 border-l-[#0f1115]">
              <div className="flex items-center gap-2 text-[#0f1115] font-bold mb-2">
                <FileText size={16} className="text-[#0f1115]" />
                Pick {data.brand} if…
              </div>
              <ul className="space-y-2 text-sm text-[#4b5563]">
                {data.pickThemBullets.map((b, i) => (
                  <li key={i} className="flex gap-2">
                    <Check size={14} className="text-[#10b981] mt-0.5 flex-shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
              {data.coexistNote && (
                <p className="mt-3 text-xs text-[#6b7280] italic">{data.coexistNote}</p>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Feature comparison table */}
      <section className="py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="font-satoshi text-2xl sm:text-3xl font-bold text-[#0f1115] mb-2">
            {data.rows.length}-feature head-to-head
          </h2>
          <p className="text-sm text-[#6b7280] mb-6">
            Updated {new Date().toLocaleString('en-US', { month: 'short', year: 'numeric' })}. Honest assessment — features Ascend doesn't have are marked&nbsp;<X size={12} className="inline text-[#9ca3af]" />.
          </p>
          <div className="border border-[#e5e7eb] bg-white overflow-x-auto" data-testid="comparison-table">
            <table className="w-full min-w-[600px]">
              <thead className="bg-[#f9fafb]">
                <tr className="text-[10px] uppercase tracking-wider text-[#6b7280] border-b border-[#e5e7eb]">
                  <th className="py-3 px-4 text-left font-bold">Feature</th>
                  <th className="py-3 px-3 text-center font-bold w-28">
                    <span className="inline-block px-2 py-0.5 bg-[#002fa7] text-white">Ascend</span>
                  </th>
                  <th className="py-3 px-3 text-center font-bold w-28">
                    <span className="inline-block px-2 py-0.5 bg-[#0f1115] text-white">{data.brand}</span>
                  </th>
                  <th className="py-3 px-3 text-center font-bold w-20">Winner</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row, i) => (
                  <tr key={i} className="border-b border-[#e5e7eb] last:border-b-0 hover:bg-[#f9fafb] transition-colors" data-testid={`compare-row-${i}`}>
                    <td className="py-3 px-4 align-top">
                      <div className="text-sm font-semibold text-[#0f1115]">{row.feature}</div>
                    </td>
                    <td className="py-3 px-3 align-top text-center">
                      <div className="flex justify-center mb-1"><CellIcon ok={row.ascend.ok} /></div>
                      <div className="text-[11px] text-[#6b7280] leading-tight">{row.ascend.note}</div>
                    </td>
                    <td className="py-3 px-3 align-top text-center">
                      <div className="flex justify-center mb-1"><CellIcon ok={row.them.ok} /></div>
                      <div className="text-[11px] text-[#6b7280] leading-tight">{row.them.note}</div>
                    </td>
                    <td className="py-3 px-3 align-top text-center">
                      <WinnerPill winner={row.winner} brand={data.brand} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Pain-band sections */}
      <section className="py-12 bg-[#f9fafb] border-y border-[#e5e7eb]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="font-satoshi text-2xl sm:text-3xl font-bold text-[#0f1115] mb-8">
            Where the difference actually shows up
          </h2>

          <div className="space-y-8">
            {data.painBands.map((band, i) => {
              const Icon = ICONS[band.icon] || Target;
              return (
                <motion.div
                  {...fadeUp}
                  key={i}
                  className="bg-white border border-[#e5e7eb] p-6"
                  data-testid={`pain-band-${i}`}
                >
                  <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-wider text-[#002fa7] font-bold">
                    <Icon size={14} /> {band.kicker}
                  </div>
                  <h3 className="font-satoshi text-xl font-bold text-[#0f1115] mb-2">
                    {band.headline}
                  </h3>
                  <p className="text-sm text-[#4b5563] leading-relaxed">{band.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 border-b border-[#e5e7eb]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="font-satoshi text-2xl sm:text-3xl font-bold text-[#0f1115] mb-6">
            Frequently asked
          </h2>
          <div className="space-y-3" data-testid="faq-list">
            {data.faqs.map(([q, a], i) => (
              <details
                key={i}
                className="bg-white border border-[#e5e7eb] p-5 group"
                data-testid={`vs-faq-${i}`}
              >
                <summary className="font-semibold text-[#0f1115] cursor-pointer flex items-start gap-2 text-sm">
                  <FileText className="w-4 h-4 text-[#002fa7] flex-shrink-0 mt-0.5" />
                  {q}
                </summary>
                <p className="mt-3 text-sm text-[#4b5563] leading-relaxed pl-6">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="bg-gradient-to-br from-[#0f1115] via-[#001d6a] to-[#002fa7] text-white p-8 sm:p-12 text-center" data-testid="vs-final-cta">
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#fbbf24] mb-3 block">
              30 SECONDS · FREE · NO SIGNUP
            </span>
            <h2 className="font-satoshi text-2xl sm:text-3xl font-black leading-tight mb-3">
              See what Ascend would tell you about your resume.
            </h2>
            <p className="text-white/80 text-sm sm:text-base mb-6 max-w-xl mx-auto">
              Compare the score and gap list to your last {data.brand} output. If our depth wins for you, you'll know in 30 seconds.
            </p>
            <Link
              to={`/free-ats-check?ref=vs_${slug}_footer&utm_source=vs_${slug}&utm_medium=footer&utm_campaign=comparison`}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-[#0f1115] text-sm font-bold hover:bg-white/90 transition-colors"
              data-testid="vs-footer-cta"
            >
              <Sparkles size={16} /> Get my free score
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Cross-link rail to peer competitors */}
      <section className="py-10 border-t border-[#e5e7eb] bg-[#f9fafb]" data-testid="vs-crosslinks">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-[#002fa7] mb-3">
            Compare Ascend to other tools
          </h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(COMPETITORS)
              .filter(([s]) => s !== slug)
              .map(([s, c]) => (
                <Link
                  key={s}
                  to={`/vs/${s}`}
                  className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-[#e5e7eb] text-sm font-semibold text-[#0f1115] hover:border-[#002fa7] transition-colors"
                  data-testid={`vs-peer-link-${s}`}
                >
                  Ascend vs {c.brand}
                  <ArrowRight size={12} />
                </Link>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default VsCompetitor;
